import React from "react";
import { WidgetWrapper } from "../../components/WidgetWrapper/WidgetWrapper";
import { EditField } from "../../components/EditPanel/types";
import { RetailerIconWidgetProps, RetailerIconWidgetState } from "./types";
import { useWidgetEditable } from "../../hooks/useWidgetEditable";
import { FONT_WEIGHT_OPTIONS_KEYWORDS } from "../../utils/fontWeightOptions";
import { themeColor } from "../../types/color";

const defaultState: RetailerIconWidgetState = {
  // Icon size
  iconSize: 18,
  iconSizeMobile: 15,

  // Overlap and spacing
  overlapRatio: 0.46,
  counterMarginLeft: 7,

  // Border styling
  iconBorderRadius: 50, // percentage - 50 = circle

  // Counter text
  counterTextColor: themeColor("textPrimary"),
  counterFontSize: 11,
  counterFontWeight: "semibold",

  // Display limits
  maxVisibleIcons: 4,
};

const editFields: EditField[] = [
  // Icon size
  {
    key: "iconSize",
    label: "Icon Size",
    type: "number",
    min: 12,
    max: 32,
    step: 2,
  },
  {
    key: "iconSizeMobile",
    label: "Icon Size (Mobile)",
    type: "number",
    min: 10,
    max: 24,
    step: 1,
  },

  // Overlap and spacing
  {
    key: "overlapRatio",
    label: "Overlap Ratio",
    type: "number",
    min: 0,
    max: 0.7,
    step: 0.05,
  },
  {
    key: "counterMarginLeft",
    label: "Counter Margin Left",
    type: "number",
    min: 0,
    max: 16,
    step: 1,
  },

  // Border styling
  {
    key: "iconBorderRadius",
    label: "Icon Border Radius (%)",
    type: "number",
    min: 0,
    max: 50,
    step: 5,
  },

  // Counter text
  { key: "counterTextColor", label: "Counter Text Color", type: "color" },
  {
    key: "counterFontSize",
    label: "Counter Font Size",
    type: "number",
    min: 8,
    max: 16,
    step: 1,
  },
  {
    key: "counterFontWeight",
    label: "Counter Font Weight",
    type: "options",
    options: [...FONT_WEIGHT_OPTIONS_KEYWORDS],
  },

  // Display limits
  {
    key: "maxVisibleIcons",
    label: "Max Visible Icons",
    type: "number",
    min: 1,
    max: 8,
    step: 1,
  },
];

export const RetailerIconWidget: React.FC<RetailerIconWidgetProps> = ({
  initialState = {},
  widgetId,
  displayName,
  children,
}) => {
  const {
    state,
    isLoading,
    isSaving,
    handleEditChange,
    handleSave,
    themeColors,
    resolveColor,
    canSave,
  } = useWidgetEditable<RetailerIconWidgetState>(
    widgetId,
    { ...defaultState, ...initialState },
    editFields,
  );

  // Show loading state while fetching theme config
  if (isLoading) {
    return <></>;
  }

  // Render the children with current state
  const widgetContent = children(state, resolveColor);

  // Wrap with WidgetWrapper for edit capability
  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={editFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      themeColors={themeColors}
    >
      {widgetContent}
    </WidgetWrapper>
  );
};
